/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { selectStyles } from "../../utils/selectStyel";

const DynamicSelectField = ({
  value,
  isEditable = true,
  handleSanitizedChange,
  options: rawOptions,
  loading = false,
  load,
}) => {
  /* =======================
     Normalize options (same logic as hook)
     ======================= */
  const options = useMemo(() => {
    if (!rawOptions) return [];

    // Case 1: IndexedDB / API shape { CONFIG: [...] }
    if (Array.isArray(rawOptions.CONFIG)) {
      return rawOptions.CONFIG.filter(
        (opt) => opt.CODE || opt.TEXT || opt.VALUE || opt.DESCRIPTION
      ).map((opt) => ({
        value: opt.CODE ?? opt.VALUE ?? opt.TEXT ?? "",
        label: opt.TEXT ?? opt.DESCRIPTION ?? opt.CODE ?? opt.VALUE ?? "",
      }));
    }

    // Case 2: array of { value, label }
    if (
      Array.isArray(rawOptions) &&
      rawOptions.every((opt) => "value" in opt && "label" in opt)
    ) {
      return rawOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    }

    // Case 3: array of strings
    if (
      Array.isArray(rawOptions) &&
      rawOptions.every((opt) => typeof opt === "string")
    ) {
      return rawOptions.map((opt) => ({
        value: opt,
        label: opt,
      }));
    }

    // Fallback
    console.warn("Unexpected dropdown format:", rawOptions);
    return [];
  }, [rawOptions]);

  /* =======================
     Fallback selected value
     ======================= */
  const [fallbackOption, setFallbackOption] = useState(null);

  useEffect(() => {
    if (!value) {
      setFallbackOption(null);
      return;
    }

    const exists = options.some((opt) => opt.value === value);

    if (!exists) {
      setFallbackOption({ value, label: String(value) });
    } else {
      setFallbackOption(null);
    }
  }, [value, options]);

  /* =======================
     Resolve selected option
     ======================= */
  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((opt) => opt.value === value) ?? fallbackOption ?? null;
  }, [value, options, fallbackOption]);

  /* =======================
     Change handler
     ======================= */
  const handleChange = (selected) => {
    handleSanitizedChange?.(selected?.value ?? "");
  };

  /* =======================
     Render
     ======================= */
  return (
    <Select
      value={selectedOption}
      isDisabled={!isEditable}
      isLoading={loading}
      options={options}
      placeholder={loading ? "Loading..." : "Select..."}
      onMenuOpen={load}
      onChange={handleChange}
      styles={selectStyles}
      menuPortalTarget={document.body}
      isClearable
    />
  );
};

export default DynamicSelectField;
